import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import type { GridAlignment } from '@mui/x-data-grid';
import {
	Box,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	IconButton,
	ListSubheader,
	Collapse,
	Chip,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { getFullUsersList } from '../../services/user.service';
import { deleteWorkDoneRecord, fetchAllWorkDoneByWorker, updateWorkDoneDetails } from '../../services/workdone.service';
import moment from 'moment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { EditWorkDoneDialog } from '../../components/Dialog/EditWorkDoneDialog';
import { styled } from '@mui/system';

export interface EditParams {
	id: number;
	userId: number;
	helperUserIds: number[];
	projectId: number;
	clientId: number;
	precinctId: number | null;
	blockId: number | null;
	typeId: number;
	subTypeId: number;
	subTypeValueId: number;
	level: number | null;
	unit: number | null;
	quantity: number;
	quantityUnit: string;
	performedAt: string;
	workCategory: 'normal' | 'free_redo' | 'rectify' | 'fabrication';
	reportedLocationText: string;
	isHip: boolean;
}

const StyledIconButton = styled(IconButton, { name: 'StyledIconButton', slot: 'Wrapper' })({
	color: 'gray',
	'&:hover': {
		color: 'red',
	},
});

const StyledIconButtonForEdit = styled(IconButton, { name: 'StyledIconButton', slot: 'Wrapper' })({
	color: 'gray',
	'&:hover': {
		color: 'blue',
	},
});

const StyledListSubheader = styled(ListSubheader)({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	color: 'blue',
	paddingTop: '8px',
	borderBottom: '1px solid #ccc',
	cursor: 'pointer',
	position: 'sticky',
	top: 0,
	zIndex: 1,
	background: '#fff',
});

export function ManageWorkDone() {
	const [pageSize, setPageSize] = useState<number>(10);
	const [loading, setLoading] = useState<boolean>(false);
	const [page, setPage] = useState<number>(0);
	const [workDoneData, setWorkDoneData] = useState<any>([]);
	const [userData, setUserData] = useState<{ Admins: any[]; Workers: any[] }>({ Admins: [], Workers: [] });
	const [initialLoad, setInitialLoad] = useState<boolean>(true);
	const [selectedWorker, setSelectedWorker] = useState<number>(0);
	const [selectedWorkerName, setSelectedWorkerName] = useState<string>('');
	const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
	const [editParams, setEditParams] = useState<EditParams | null>(null);
	const [itemsTotal, setItemsTotal] = useState<number>(1);

	const [showList, setShowList] = useState<boolean>(false);

	const [showAdmins, setShowAdmins] = useState<boolean>(true);
	const [showWorkers, setShowWorkers] = useState<boolean>(true);

	const columns = [
		{ field: 'performedAt', headerName: 'Date', width: 140 },
		{
			field: 'projectSummary',
			headerName: 'Project / client',
			minWidth: 155,
			flex: 0.9,
			sortable: false,
			renderCell: (params: any) => (
				<Box sx={{ minWidth: 0 }}>
					<Typography variant='body2' fontWeight={600} noWrap>
						{params.row.projectName || '—'}
					</Typography>
					<Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
						{params.row.clientName || 'No client'}
					</Typography>
				</Box>
			),
		},
		{
			field: 'workSummary',
			headerName: 'Work',
			minWidth: 170,
			flex: 1,
			sortable: false,
			renderCell: (params: any) => {
				const detail = [params.row.subTypeNames, params.row.subTypeValue].filter(Boolean).join(' · ');
				const quantity = params.row.quantityDisplay !== '—' ? params.row.quantityDisplay : null;
				return (
					<Box sx={{ minWidth: 0 }}>
						<Typography variant='body2' fontWeight={600} noWrap>
							{params.row.typeName || '—'}
						</Typography>
						<Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
							{[detail, quantity].filter(Boolean).join(' · ') || '—'}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: 'locationSummary',
			headerName: 'Location',
			minWidth: 225,
			flex: 1.25,
			sortable: false,
			renderCell: (params: any) => {
				const structured = [
					params.row.blockNumber != null ? `Block ${params.row.blockNumber}` : null,
					params.row.level != null ? `Level ${params.row.level}` : null,
					params.row.unit != null ? `Unit ${params.row.unit}` : null,
				]
					.filter(Boolean)
					.join(' · ');
				const area = params.row.location || params.row.reportedLocationText || params.row.precinctName;
				return (
					<Tooltip title={[area, structured].filter(Boolean).join(' · ')}>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant='body2' fontWeight={600} noWrap>
								{structured || area || '—'}
							</Typography>
							{structured && area && (
								<Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
									{area}
								</Typography>
							)}
						</Box>
					</Tooltip>
				);
			},
		},
		{
			field: 'participants',
			headerName: 'Workers',
			minWidth: 115,
			flex: 0.8,
			sortable: false,
			renderCell: (params: any) => {
				const participants = (params.value ?? []) as any[];
				const names = participants.map(
					(participant) =>
						participant.name ?? participant.user?.name ?? participant.userName ?? participant.workerName ?? 'Worker'
				);
				if (names.length === 0) return <Typography color='text.secondary'>—</Typography>;
				return (
					<Tooltip title={names.join(', ')}>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant='body2' fontWeight={600} noWrap>
								{names[0]}
							</Typography>
							{names.length > 1 && (
								<Typography variant='caption' color='text.secondary'>
									{names.length} workers total
								</Typography>
							)}
						</Box>
					</Tooltip>
				);
			},
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 140,
			align: 'center' as GridAlignment,
			headerAlign: 'center' as GridAlignment,
			renderCell: (params: any) => (
				<Stack direction='column' spacing={0.5} alignItems='center'>
					<Chip
						size='small'
						label={params.row.isClientBillable ? 'Client billable' : 'Non-billable'}
						color={params.row.isClientBillable ? 'success' : 'default'}
						variant='outlined'
					/>
					{params.row.workCategory !== 'normal' && (
						<Chip
							size='small'
							label={String(params.row.workCategory).replace('_', ' ')}
							color='warning'
							variant='outlined'
						/>
					)}
				</Stack>
			),
		},
		{
			field: 'actions',
			headerName: 'Actions',
			width: 90,
			align: 'center' as GridAlignment,
			headerAlign: 'center' as GridAlignment,
			sortable: false,
			renderCell: (params: any) =>
				params.row?.Edit ? (
					<Stack direction='row' spacing={0}>
						<Tooltip title='Edit work done'>
							<StyledIconButtonForEdit aria-label='Edit work done' onClick={() => handleEdit(params.row)}>
								<EditIcon fontSize='small' />
							</StyledIconButtonForEdit>
						</Tooltip>
						<Tooltip title='Delete work done'>
							<StyledIconButton aria-label='Delete work done' onClick={() => handleDelete(params.row.id)}>
								<DeleteIcon fontSize='small' />
							</StyledIconButton>
						</Tooltip>
					</Stack>
				) : (
					<Typography variant='caption' color='text.secondary'>
						Claimed
					</Typography>
				),
		},
	];

	const loadDataToGrid = async (workerId: number, pageNumber: number) => {
		try {
			setLoading(true);
			const result: any = await fetchAllWorkDoneByWorker(workerId, pageNumber, pageSize);

			if (result.success) {
				let tmpList: any = [];
				if (result?.data?.rows?.length === 0 && page > 0) {
					handlePageChange(page - 1);
					return;
				}

				for (let i = 0; i < result?.data?.rows?.length; i++) {
					const info = result.data.rows[i];
					const quantity = Number(info.quantity ?? 1);
					const quantityUnit = info.quantityUnit ?? 'unit';
					const hasExactUnit = info.blockId != null && info.level != null && info.unit != null;

					const row = {
						id: info.id,
						userId: info.userId,
						projectId: info.projectId,
						clientId: info.clientId,
						precinctId: info.precinctId,
						blockId: info.blockId,
						typeId: info.typeId,
						subTypeId: info.subTypeId,
						subTypeValueId: info.subTypeValueId,
						isHip: Boolean(info.isHip),
						progressPaymentNum: info.progressPaymentNumber,
						claimRefNum: info.claimRefNum,
						projectName: info.projectName,
						clientName: info.clientName,
						precinctName: info.precinctName,
						location: info.location,
						typeName: info.typeName,
						subTypeNames: info.subTypeNames,
						subTypeValue: info.subTypeValue,
						blockNumber: info.blockNumber,
						level: info.level,
						levelTo: info.levelTo,
						unit: info.unit,
						quantityDisplay: hasExactUnit && quantity === 1 ? '—' : `${quantity} ${quantityUnit}`,
						quantity,
						quantityUnit,
						performedAt: moment(info.performedAt ?? info.createdAt).format('DD-MM-YYYY HH:mm'),
						performedAtRaw: info.performedAt ?? info.createdAt,
						workCategory: info.workCategory ?? (info.statusType === 1 ? 'free_redo' : 'normal'),
						isClientBillable: info.isClientBillable ?? info.statusType !== 1,
						isWorkerCreditable: info.isWorkerCreditable ?? true,
						reviewStatus: info.reviewStatus ?? null,
						reportedLocationText: info.reportedLocationText ?? '',
						participants:
							info.participants ??
							info.workDoneParticipants ??
							(info.workerName
								? [
										{
											userId: info.userId,
											name: info.workerName,
											role: 'primary',
											isCredited: true,
										},
								  ]
								: []),
						unitFrom: info.unitFrom,
						unitTo: info.unitTo,
						createdAt: moment(info.createdAt).format('DD-MM-YYYY'),
						updatedAt: moment(info.updatedAt).format('DD-MM-YYYY'),
						Edit: info.claimId > 0 ? false : true,
					};

					tmpList.push(row);
				}

				setItemsTotal(result.data.totalItems || 0);
				setWorkDoneData([...tmpList]);
			}
			setLoading(false);
		} catch (err: any) {
			toast.error(err?.response?.data?.message || err?.message || 'Something went wrong');
			setLoading(false);
		}
	};

	const handlePageChange = (pageNumber: any) => {
		setPage(pageNumber);
		loadDataToGrid(selectedWorker, pageNumber);
	};

	const loadUsers = async () => {
		try {
			setLoading(true);
			const result = await getFullUsersList();

			if (result.success) {
				const admins = result.data.rows.filter((user: any) => user.type === 'admin');
				const workers = result.data.rows.filter((user: any) => user.type === 'worker');

				setUserData({ Admins: admins, Workers: workers });
			}
			setLoading(false);
		} catch (err: any) {
			console.error(err);
			toast.error('An error occurred while fetching users data.');
		}
	};

	const handleEdit = (row: any) => {
		const participants = row.participants ?? [];
		setEditParams({
			id: row.id,
			userId: row.userId,
			helperUserIds: participants
				.filter((participant: any) => participant.userId !== row.userId)
				.map((participant: any) => participant.userId),
			projectId: row.projectId,
			clientId: row.clientId,
			precinctId: row.precinctId,
			blockId: row.blockId,
			typeId: row.typeId,
			subTypeId: row.subTypeId,
			subTypeValueId: row.subTypeValueId,
			level: row.level,
			unit: row.unit,
			quantity: row.quantity,
			quantityUnit: row.quantityUnit,
			performedAt: row.performedAtRaw,
			workCategory: row.workCategory,
			reportedLocationText: row.reportedLocationText,
			isHip: row.isHip,
		});
		setShowEditDialog(true);
	};

	const handleDelete = async (id: number) => {
		try {
			const result = await deleteWorkDoneRecord(id);
			if (result.success) {
				toast.success('Work done record deleted successfully!');
				loadDataToGrid(selectedWorker, page);
			} else {
				toast.error(result.message);
			}
		} catch (err: any) {
			console.error(err);
			toast.error('An error occurred while deleting work done record.');
		}
	};

	const handleDialogClose = () => {
		setShowEditDialog(false);
		loadDataToGrid(selectedWorker, page);
	};

	const onSubmit = async (payload: EditParams) => {
		try {
			const { isHip: _isHip, ...request } = payload;
			const response = await updateWorkDoneDetails(request);
			if (response.success) {
				toast.success('Work done record updated successfully!');
				setShowEditDialog(false);
				setEditParams(null);
				await loadDataToGrid(selectedWorker, page);
			}
		} catch (e: any) {
			toast.error(e?.response?.data?.message || 'Error updating the work done record!');
			throw e;
		}
	};

	const handleCategoryClick = (category: string, event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		console.log(`Category clicked: ${category}`);
		if (category === 'Admins') {
			setShowAdmins(!showAdmins);
		} else {
			setShowWorkers(!showWorkers);
		}
	};

	useEffect(() => {
		loadUsers();
		setInitialLoad(false);
	}, []);

	useEffect(() => {
		if (!initialLoad) {
			loadDataToGrid(selectedWorker, page);
		}
	}, [selectedWorker, page, pageSize]);

	const handleWorkerSelect = (userId: number, userName: string) => {
		setSelectedWorkerName(userName);
		setSelectedWorker(userId);
		setShowList(false);
	};

	return (
		<Box sx={{ flex: 1, height: '100%' }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<h2>All Work Done</h2>
				<FormControl fullWidth variant='outlined' sx={{ width: '300px', height: '50px' }}>
					<InputLabel id='worker-select-label' sx={{ backgroundColor: 'white', paddingRight: '10px' }}>
						Select Admin / Worker
					</InputLabel>
					<Select
						labelId='worker-select-label'
						id='worker-select'
						value={selectedWorker}
						onClick={() => setShowList(!showList)}
						open={showList}
						label='Select Worker'
						renderValue={() => selectedWorkerName || 'Select Admin / Worker'}>
						<MenuItem disabled value={0}>
							Select Admin / Worker
						</MenuItem>
						<StyledListSubheader id='admins' key={'Admins'} onClick={(e) => handleCategoryClick('Admins', e)}>
							Admins ({userData.Admins.length}) {showAdmins ? <ExpandLess /> : <ExpandMore />}
						</StyledListSubheader>
						<Collapse id='admins' key={'Admins'} in={showAdmins} timeout='auto' unmountOnExit>
							{userData.Admins.map((admin) => (
								<MenuItem key={admin.id} value={admin.id} onClick={() => handleWorkerSelect(admin.id, admin.name)}>
									{admin.name}
								</MenuItem>
							))}
						</Collapse>
						<StyledListSubheader
							id='workers'
							key={'Workers'}
							tabIndex={0}
							onClick={(e) => handleCategoryClick('Workers', e)}>
							Workers ({userData.Workers.length}) {showWorkers ? <ExpandLess /> : <ExpandMore />}
						</StyledListSubheader>
						<Collapse id='workers' key={'Workers'} in={showWorkers} timeout='auto' unmountOnExit>
							{userData.Workers.map((worker) => (
								<MenuItem key={worker.id} value={worker.id} onClick={() => handleWorkerSelect(worker.id, worker.name)}>
									{worker.name}
								</MenuItem>
							))}
						</Collapse>
					</Select>
				</FormControl>
			</Box>
			<Box sx={{ height: 600, width: '100%', mt: 2 }}>
				<DataGrid
					rows={workDoneData}
					columns={columns}
					loading={loading}
					rowCount={itemsTotal}
					page={page}
					pageSize={pageSize}
					rowsPerPageOptions={[10, 20, 50, 100]}
					pagination
					paginationMode='server'
					onPageSizeChange={(size) => setPageSize(size)}
					onPageChange={handlePageChange}
					getRowHeight={() => 'auto'}
					sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
					localeText={{
						noRowsLabel: selectedWorkerName === '' ? 'Select A Worker' : 'Data not available',
					}}
				/>
			</Box>
			{showEditDialog && editParams && (
				<EditWorkDoneDialog show={showEditDialog} params={editParams} onSubmit={onSubmit} onClose={handleDialogClose} />
			)}
		</Box>
	);
}
